import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AdminProviders from "@/components/admin/AdminProviders";
import AdminRequests from "@/components/admin/AdminRequests";
import AdminServices from "@/components/admin/AdminServices";
import AdminSuggestions from "@/components/admin/AdminSuggestions";
import AdminCities from "@/components/admin/AdminCities";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminPayments from "@/components/admin/AdminPayments";

const STATS = [
  ["providers_pending", "Prestadores em análise", "providers", "pending"],
  ["providers_available", "Disponíveis agora", "providers", "disponiveis"],
  ["requests_open", "Pedidos em aberto", "requests", "aberto"],
  ["requests_no_provider", "Sem prestador", "requests", "sem_prestador"],
  ["suggestions_pending", "Sugestões de serviço", "suggestions", ""],
];

const TABS = [
  ["providers", "Prestadores", AdminProviders],
  ["requests", "Pedidos", AdminRequests],
  ["services", "Serviços", AdminServices],
  ["suggestions", "Sugestões", AdminSuggestions],
  ["payments", "Pagamentos", AdminPayments],
  ["cities", "Cidades", AdminCities],
  ["settings", "Configurações", AdminSettings],
];

export default function Admin() {
  const { user } = useAuth();
  const { data: ov } = useQuery({ queryKey: ["admin-overview"], queryFn: () => api.get("/admin/overview").then((r) => r.data), enabled: !!user?.is_admin, refetchInterval: 15000 });
  const [tab, setTab] = useState("providers");
  const [filters, setFilters] = useState({ providers: "", requests: "" });
  const setFilter = (t) => (v) => setFilters((f) => ({ ...f, [t]: v }));
  if (!user.is_admin) return <Navigate to="/" replace />;
  const openStat = (t, f) => {
    setTab(t);
    if (t in filters) setFilter(t)(f);
    setTimeout(() => document.getElementById("admin-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <h1 className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">Painel do administrador</h1>
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
        {STATS.map(([k, label, t, f]) => (
          <button key={k} type="button" onClick={() => openStat(t, f)} className="po-card text-left transition-colors hover:border-orange-400 hover:bg-orange-50" data-testid={`admin-stat-${k}`}>
            <p className="font-display text-3xl font-extrabold text-orange-600" data-testid={`admin-stat-${k}-value`}>{ov?.[k] ?? "–"}</p>
            <p className="text-base font-semibold text-slate-700">{label}</p>
            <p className="mt-1 text-sm font-bold text-orange-600">Ver lista →</p>
          </button>
        ))}
      </div>
      <Tabs value={tab} onValueChange={setTab} className="mt-6 scroll-mt-40" id="admin-tabs">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-2xl bg-slate-100 p-1.5">
          {TABS.map(([v, label]) => (
            <TabsTrigger key={v} value={v} className="min-h-[48px] rounded-xl px-4 text-base font-bold data-[state=active]:bg-orange-500 data-[state=active]:text-white" data-testid={`admin-tab-${v}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
        {TABS.map(([v, , C]) => (
          <TabsContent key={v} value={v} className="mt-5">
            <C {...(v in filters ? { filter: filters[v], setFilter: setFilter(v), counts: ov } : {})} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
